import React, { useEffect, useState } from 'react';
import { useAuth } from '@/context/AuthContext';
import { Navigate } from 'react-router-dom';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { supabase } from '@/integrations/supabase/client';

interface TokenData {
  name: string;
  lightValue: string;
  darkValue: string;
  type: 'color' | 'other';
}

const AdminTokens = () => {
  const { user, loading } = useAuth();
  const [tokens, setTokens] = useState<TokenData[]>([]);
  const [isAdmin, setIsAdmin] = useState<boolean | null>(null);

  // Server-side admin check via user_roles table (RLS-enforced)
  useEffect(() => {
    if (!user) {
      setIsAdmin(false);
      return;
    }
    let cancelled = false;
    (async () => {
      const { data, error } = await supabase
        .from('user_roles')
        .select('role')
        .eq('user_id', user.id)
        .eq('role', 'admin')
        .maybeSingle();
      if (cancelled) return;
      setIsAdmin(!error && !!data);
    })();
    return () => { cancelled = true; };
  }, [user]);

  useEffect(() => {
    const extractTokens = () => {
      const tokenList: TokenData[] = [];
      
      // Get all CSS custom properties from the root element
      const rootStyles = Array.from(document.styleSheets)
        .flatMap(sheet => {
          try {
            return Array.from(sheet.cssRules);
          } catch {
            return [];
          }
        })
        .filter((rule): rule is CSSStyleRule => rule.type === CSSRule.STYLE_RULE)
        .filter(rule => rule.selectorText === ':root' || rule.selectorText === '.dark')
        .flatMap(rule => Array.from(rule.style))
        .filter(prop => prop.startsWith('--'));

      // Get unique property names
      const uniqueProps = Array.from(new Set(rootStyles));

      uniqueProps.forEach(prop => {
        // Get light mode value (from :root)
        const lightComputedStyle = getComputedStyle(document.documentElement);
        const lightValue = lightComputedStyle.getPropertyValue(prop).trim();
        
        // Temporarily add dark class to get dark mode value
        document.documentElement.classList.add('dark');
        const darkComputedStyle = getComputedStyle(document.documentElement);
        const darkValue = darkComputedStyle.getPropertyValue(prop).trim();
        document.documentElement.classList.remove('dark');
        
        if (lightValue || darkValue) {
          const isColor = (lightValue && (lightValue.includes('hsl') || /^\d+(\.\d+)?\s+\d+(\.\d+)?%\s+\d+(\.\d+)?%/.test(lightValue))) ||
                         (darkValue && (darkValue.includes('hsl') || /^\d+(\.\d+)?\s+\d+(\.\d+)?%\s+\d+(\.\d+)?%/.test(darkValue)));
          
          tokenList.push({
            name: prop,
            lightValue: lightValue || 'N/A',
            darkValue: darkValue || 'N/A',
            type: isColor ? 'color' : 'other'
          });
        }
      });

      // Sort tokens: colors first, then alphabetically
      tokenList.sort((a, b) => {
        if (a.type === 'color' && b.type !== 'color') return -1;
        if (a.type !== 'color' && b.type === 'color') return 1;
        return a.name.localeCompare(b.name);
      });

      setTokens(tokenList);
    };

    extractTokens();
  }, []);

  // Show loading while auth state is being determined
  if (loading || (user && isAdmin === null)) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-muted-foreground">Loading...</div>
      </div>
    );
  }

  if (!user || !isAdmin) {
    return <Navigate to="/" replace />;
  }

  const hslToHex = (hslString: string): string => {
    if (!hslString || hslString === 'N/A') return 'N/A';
    
    // Extract HSL values from string like "220 14.3% 95.9%" or "hsl(220 14.3% 95.9%)"
    const hslMatch = hslString.match(/(\d+(?:\.\d+)?)\s+(\d+(?:\.\d+)?)%\s+(\d+(?:\.\d+)?)%/);
    if (!hslMatch) return 'N/A';
    
    const h = parseFloat(hslMatch[1]) / 360;
    const s = parseFloat(hslMatch[2]) / 100;
    const l = parseFloat(hslMatch[3]) / 100;
    
    const c = (1 - Math.abs(2 * l - 1)) * s;
    const x = c * (1 - Math.abs((h * 6) % 2 - 1));
    const m = l - c / 2;
    
    let r = 0, g = 0, b = 0;
    
    if (0 <= h && h < 1/6) {
      r = c; g = x; b = 0;
    } else if (1/6 <= h && h < 2/6) {
      r = x; g = c; b = 0;
    } else if (2/6 <= h && h < 3/6) {
      r = 0; g = c; b = x;
    } else if (3/6 <= h && h < 4/6) {
      r = 0; g = x; b = c;
    } else if (4/6 <= h && h < 5/6) {
      r = x; g = 0; b = c;
    } else if (5/6 <= h && h < 1) {
      r = c; g = 0; b = x;
    }
    
    const toHex = (val: number) => Math.round((val + m) * 255).toString(16).padStart(2, '0');
    return `#${toHex(r)}${toHex(g)}${toHex(b)}`.toUpperCase();
  };

  const ColorSwatch = ({ value }: { value: string }) => {
    const hslValue = value.includes('hsl') ? value : `hsl(${value})`;
    
    return (
      <div className="flex items-center gap-2">
        <div 
          className="w-6 h-6 rounded border border-border"
          style={{ backgroundColor: hslValue }}
        />
        <code className="text-sm text-muted-foreground">{value}</code>
      </div>
    );
  };

  return (
    <div className="container mx-auto py-8">
      <Card>
        <CardHeader>
          <CardTitle>Design System Tokens</CardTitle>
          <p className="text-muted-foreground">
            Live view of all design tokens currently loaded in the system
          </p>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Token Name</TableHead>
                <TableHead>Light Mode</TableHead>
                <TableHead>Dark Mode</TableHead>
                <TableHead>Hex Values</TableHead>
                <TableHead>Type</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {tokens.map((token) => (
                <TableRow key={token.name}>
                  <TableCell>
                    <code className="text-sm font-mono">{token.name}</code>
                  </TableCell>
                  <TableCell>
                    {token.type === 'color' ? (
                      <ColorSwatch value={token.lightValue} />
                    ) : (
                      <code className="text-sm text-muted-foreground">{token.lightValue}</code>
                    )}
                  </TableCell>
                  <TableCell>
                    {token.type === 'color' ? (
                      <ColorSwatch value={token.darkValue} />
                    ) : (
                      <code className="text-sm text-muted-foreground">{token.darkValue}</code>
                    )}
                  </TableCell>
                  <TableCell>
                    {token.type === 'color' ? (
                      <div className="space-y-1">
                        <div className="text-xs text-muted-foreground">Light: <code>{hslToHex(token.lightValue)}</code></div>
                        <div className="text-xs text-muted-foreground">Dark: <code>{hslToHex(token.darkValue)}</code></div>
                      </div>
                    ) : (
                      <span className="text-xs text-muted-foreground">N/A</span>
                    )}
                  </TableCell>
                  <TableCell>
                    <span className={`inline-flex px-2 py-1 rounded-full text-xs font-medium ${
                      token.type === 'color' 
                        ? 'bg-primary/10 text-primary' 
                        : 'bg-secondary text-secondary-foreground'
                    }`}>
                      {token.type}
                    </span>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminTokens;